import { ComponentPropsWithoutRef, ReactNode, FC, useRef } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { styled } from 'styled-components'

import { boxShadow, onSurface, onSurfaceVariant, overlay, surface } from 'core/styles'

type DrawerProps = {
  isOpen: boolean
  title: ReactNode
  subtitle?: ReactNode
  actionButtons?: ReactNode
  children: ReactNode
  onClose: () => void
  width?: string | number
  preventClose?: boolean
  // This isn't a super necessary prop to have to control as of 2026.
  // But if you need to control the auto focus behavior, you can use this prop.
  // Radix Dialog docs: https://www.radix-ui.com/primitives/docs/components/dialog#content
  onOpenAutoFocus?: ComponentPropsWithoutRef<typeof DialogPrimitive.Content>['onOpenAutoFocus']
}

const Drawer: FC<DrawerProps> = ({
  isOpen,
  title,
  subtitle,
  actionButtons,
  children,
  onClose,
  width = 800,
  preventClose = false,
  onOpenAutoFocus,
}) => {
  const titleRef = useRef<HTMLHeadingElement | null>(null)

  return (
    <DialogPrimitive.Root
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) onClose()
      }}
      open={isOpen}
    >
      <DialogPrimitive.Portal>
        <Overlay />
        <Content
          $width={width}
          onOpenAutoFocus={(event) => {
            if (onOpenAutoFocus) {
              onOpenAutoFocus(event)
              return
            }

            event.preventDefault()
            requestAnimationFrame(() => titleRef.current?.focus())
          }}
          onPointerDownOutside={(event) => {
            if (preventClose) event.preventDefault()
          }}
        >
          <Header>
            <HeaderLeft>
              <TitleContainer>
                <DialogPrimitive.Title asChild>
                  <Title ref={titleRef} tabIndex={-1}>
                    {title}
                  </Title>
                </DialogPrimitive.Title>
              </TitleContainer>
              {!!subtitle && (
                <DialogPrimitive.Description asChild>
                  <Subtitle>{subtitle}</Subtitle>
                </DialogPrimitive.Description>
              )}
            </HeaderLeft>
            <HeaderRight>{actionButtons}</HeaderRight>
          </Header>
          <Body>{children}</Body>
        </Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default Drawer

const Overlay = styled(DialogPrimitive.Overlay)`
  position: fixed;
  inset: 0;
  z-index: 10;
  background: ${overlay};
  cursor: pointer;
  pointer-events: auto;
`

const Content = styled(DialogPrimitive.Content)<{
  $width: string | number
}>`
  display: flex;
  position: fixed;
  top: 0;
  right: 0;
  flex-direction: column;
  z-index: 10;
  outline: none;
  box-shadow: ${boxShadow};
  background: ${surface};
  width: ${(p) => (typeof p.$width === 'number' ? `${p.$width}px` : p.$width)};
  height: 100vh;
`

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 16px;
  align-items: start;
  justify-content: space-between;
  padding: 36px 40px 16px;
`

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`

const TitleContainer = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
  min-width: 0;
`

const HeaderRight = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 12px;
  align-items: center;
  padding-top: 4px;
`

const Title = styled.h2`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${onSurface};
  font-size: 24px;
  font-weight: 700;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`

const Subtitle = styled.div`
  margin-top: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${onSurfaceVariant};
  font-size: 12px;
  font-weight: 400;
`

const Body = styled.div`
  flex: 1;
  padding: 20px 40px 36px;
  overflow-y: auto;
`
